import { IProjectSettings, ITodoCreatedClosed } from './Interfaces';
import { Plugin } from './Plugin';
import {
  IDataStorage,
  XRTodo,
  refLinkStyle,
  refLinkTooltip,
  Notifications,
  UIToolsConstants,
  IReference,
  TodoTypes,
  IApp,
} from 'matrix-requirements-sdk/client';
import {
  IDashboardPage,
  IDashboardParametersBase,
  Project,
  GetTodosAck,
  Todo,
  NotificationsBL,
} from 'matrix-requirements-sdk/client';
import { sdkInstance } from './Instance';
import { PagedLocalTodos } from './PagedLocalTodos';

interface IDashboardParameters extends IDashboardParametersBase {
  selection: IReference[];
}

// eslint-disable-next-line no-unused-vars
export class DashboardPage implements IDashboardPage<IDashboardParameters> {
  settings: IProjectSettings;

  _root: JQuery;

  pagedTodos: PagedLocalTodos;

  static calculateCumulativeChart(lst: Todo[]) {
    let dates = lst.map((item) => {
      return moment(item.createdAt);
    });

    //
    // Make sure the dates array includes any date when something was created or closed (or both).
    //
    dates = dates.concat(
      lst
        .filter((o) => {
          return o.closedAt != undefined;
        })
        .map((item) => {
          return moment(item.closedAt);
        }),
    );

    //
    // filter out garbage
    //
    const mapDates = dates
      .map((item) => {
        return moment([item.year(), item.month(), item.date()]);
      })
      .filter((m) => {
        return m.isValid();
      });

    //
    // Remove duplicates and establish the structure.
    //
    let filterMapDates: ITodoCreatedClosed[] = [];
    let datePresentMap = {};
    for (let index = 0; index < mapDates.length; index++) {
      const element = mapDates[index];
      const key = element.format('YYYYMMDD');
      if (datePresentMap[key] === undefined) {
        datePresentMap[key] = true;
        filterMapDates.push({ date: element, created: 0, remaining: 0 });
      }
    }

    //
    // Sort the dates.
    //
    filterMapDates = filterMapDates.sort((a, b) => {
      return a.date.valueOf() - b.date.valueOf();
    });

    //
    // Create a map of day -> { counts }
    //
    let filterMapDatesMap = {};
    filterMapDates.forEach((d) => {
      filterMapDatesMap[d.date.format('YYYYMMDD')] = {
        date: d.date,
        createdCount: 0,
        closedCount: 0,
      };
    });

    //
    // Fill in the (non-summed) creation and closing counts for each day.
    //
    for (let i = 0; i < lst.length; i++) {
      var creationDate = moment(lst[i].createdAt);
      const d = moment([
        creationDate.year(),
        creationDate.month(),
        creationDate.date(),
      ]);
      let itemInMap = filterMapDatesMap[d.format('YYYYMMDD')];
      itemInMap.createdCount++;

      if (lst[i].closedAt) {
        // Not every todo has been closed.
        const closedAt = moment(lst[i].closedAt);
        const cd = moment([closedAt.year(), closedAt.month(), closedAt.date()]);
        itemInMap = filterMapDatesMap[cd.format('YYYYMMDD')];
        itemInMap.closedCount++;
      }
    }

    //
    // Put this data into the filterMapDates with cumulative summing.
    //
    let prevCreatedCount = 0;
    let prevClosedCount = 0;
    for (let index = 0; index < filterMapDates.length; index++) {
      const key = filterMapDates[index].date.format('YYYYMMDD');
      // Find the data.
      let itemInMap = filterMapDatesMap[key];
      if (itemInMap) {
        const nbCreated = prevCreatedCount + itemInMap.createdCount;
        filterMapDates[index].created = nbCreated;
        prevCreatedCount = nbCreated;

        const nbClosed = prevClosedCount + itemInMap.closedCount;
        prevClosedCount = nbClosed;

        const nbRemaining = nbCreated - nbClosed;
        filterMapDates[index].remaining = nbRemaining;
      }
    }

    const date: any[] = ['Date'];
    const created: any[] = ['Created'];
    const remainings: any[] = ['Pending'];
    for (let i = 0; i < filterMapDates.length; i++) {
      date.push(filterMapDates[i].date.toDate());
      created.push(filterMapDates[i].created);
      remainings.push(filterMapDates[i].remaining);
    }

    return { date, created, remainings };
  }
  constructor(
    private project: Project,
    private projectStorage: IDataStorage,
    private popupModeOrControl = false,
    private currentFolder: string = undefined,
  ) {
    this.settings = {
      ...Plugin.config.projectSettingsPage.defaultSettings,
      ...project
        .getItemConfig()
        .getSettingJSON(Plugin.config.projectSettingsPage.settingName, {}),
    };
    this.pagedTodos = new PagedLocalTodos(100, (todos) =>
      this.renderNotifTable(todos),
    );
  }

  /** Customize static HTML here */
  private getDashboardDOM(): JQuery {
    return $(this.dashboardDOM);
  }

  /** Add interactive element in this function */
  renderProjectPage() {
    sdkInstance.app.itemForm.append(
      sdkInstance.ml.UI.getPageTitle(
        Plugin.config.dashboard.title,
        () => {
          return this._root;
        },
        () => {
          this.onResize();
        },
      ),
    );
    sdkInstance.app.itemForm.append(this.render());
  }

  eventsAttached: boolean;

  public async loadNotifications() {
    $('#waitForIt').html('');
    $('#waitForIt').append(sdkInstance.ml.UI.getSpinningWait('please wait...'));
    $('#NOOContent').hide();
    $('#NOONoItems').hide();

    $('.spinningWait').show();

    this.pagedTodos.setAllElements([]);

    $('.addedNotifRow').remove();

    const allNotifications: GetTodosAck = await this.project.getTodos(
      undefined,
      true,
      true,
    );
    if (allNotifications.todos.length > 0) {
      this.pagedTodos.setAllElements(allNotifications.todos.reverse());

      // The call to setPageStart() kicks off the render of the notification table through the callback we
      // passed to pagedTodos.
      this.pagedTodos.setPageStart(0);

      $('#NOOContent').show();

      // The charts don't care about the filters, they work on all the todos.
      const redraw = () => {
        this.drawNotifCharts(this.pagedTodos.getAllElements());
        this.drawCumulativeChart(this.pagedTodos.getAllElements());
      };
      redraw();

      $('.spinningWait').hide();

      if (!this.eventsAttached) {
        $(document).on('chartResize', redraw);
        $(document).on('contextFrameResized', redraw);
        $(document).on('sidebarResized', redraw);
        this.eventsAttached = true;
      }
    } else {
      $('.spinningWait').hide();
      $('#NOONoItems').show();
    }
  }
  start() {
    // No automatic start in case of popup
    if (this.popupModeOrControl) return;
    // Get last opened Notif for that user.
    this.loadNotifications();
  }

  renderNotifTable(lst: Todo[]) {
    this.updateNavState();

    $('.addedNotifRow').remove();
    const app = <IApp>sdkInstance.app;

    for (let i = 0; i < lst.length; i++) {
      const notif = lst[i];
      const template = $('#NotifRow').clone();
      template.find("[data-attr='itemRef']").html('');

      const jId = $('<span>' + notif.itemRef + '</span>').refLink({
        id: notif.itemRef,
        title: app.getItemTitle(notif.itemRef),
        style: sdkInstance['refLinkStyle'].link,
        tooltip: sdkInstance['refLinkTooltip'].html,
        hideTitle: false,
      });

      template.find("[data-attr='itemRef']").append(jId);

      template
        .find("[data-attr='title']")
        .html(sdkInstance['NotificationsBL'].getMessage(<XRTodo>notif));
      template.find("[data-attr='assignee']").html(notif.login); //+ " --> " + Notif.creationDate.format("YYYYMMDD") + "|" + Notif.executionDate.format("YYYYMMDD"));
      template.find("[data-attr='date']").html('');
      template
        .find("[data-attr='date']")
        .html('')
        .append('Created ' + moment(new Date(notif.createdAt)).fromNow())
        .attr('title', notif.createdAtUserFormat);

      if (notif.closedAt == undefined) {
        template
          .find("[data-attr='date']")
          .append(
            ' and completed ' + moment(new Date(notif.createdAt)).fromNow(),
          )
          .attr('title', notif.createdAtUserFormat);
      }
      template.find("[data-attr='assignedBy']").html(notif.originatorLogin); //+ " --> " + Notif.creationDate.format("YYYYMMDD") + "|" + Notif.executionDate.format("YYYYMMDD"));

      let typeIcon = '';
      switch (notif.action.todoType) {
        case sdkInstance['TodoTypes'].needReview:
        case sdkInstance['TodoTypes'].reviewsDone:
          typeIcon = 'fal fa-comment-alt-lines';
          break;
        case sdkInstance['TodoTypes'].user:
          typeIcon = 'fal fa-tasks';
          break;
        case sdkInstance['TodoTypes'].needSignature:
        case sdkInstance['TodoTypes'].signaturesDone:
        case sdkInstance['TodoTypes'].signatureRejected:
          typeIcon = 'fal fa-file-signature';
          break;
        case sdkInstance['TodoTypes'].needApprove:
        case sdkInstance['TodoTypes'].approveDone:
          typeIcon = 'fal fa-file-f-check';
          break;

        case sdkInstance['TodoTypes'].needTraining:
          typeIcon = 'fal fa-chalkboard-teacher';
          break;
        default:
          typeIcon = 'fal fa-tasks';
          break;
      }

      template.find("[data-attr='type']").html("<i class='" + typeIcon + "'/>"); //+ " --> " + Notif.creationDate.format("YYYYMMDD") + "|" + Notif.executionDate.format("YYYYMMDD"));
      template
        .find("[data-attr='type'] i")
        .css(
          'color',
          notif.closedAt != undefined
            ? 'var(--GreenSushi)'
            : 'var(--RedPersimmon)',
        );

      template.removeClass('hidden');
      template.addClass('addedNotifRow');
      $('#NotifList').append(template);
    }
    if (lst.length == 0) {
      $('#NotifList').append(
        '<tr class="addedNotifRow" class="hidden"> <td colspan="4"> No data to display</td></tr>',
      );
    }
  }

  filterNotifByUserByStatus(filter: any) {
    this.pagedTodos.clearFilteredElements();
    if (filter) {
      if (filter.user != '') {
        this.pagedTodos.cumulativeFilterElements((o) => {
          return o.login == filter.user;
        });
      }
      if (filter.status == 'Pending') {
        this.pagedTodos.cumulativeFilterElements((o) => {
          return o.closedAt == undefined;
        });
      }
      if (filter.status == 'Done') {
        this.pagedTodos.cumulativeFilterElements((o) => {
          return o.closedAt != undefined;
        });
      }
    }
    // go to the top of the paging system
    this.pagedTodos.setPageStart(0);
  }

  public render(): JQuery {
    this._root = this.getDashboardDOM();

    $('#NOOContent').hide();
    const baseControl = $('#itemSelectionNOO');

    if (this.popupModeOrControl) {
      baseControl.hide();
    }
    //Table filter
    const todos = this.pagedTodos;
    $('#NOOInputFilter', this._root).on('keyup', function (e) {
      const value = $(e.target).val().toLowerCase();
      todos.updateSearchFilter(value);
    });

    $(document).on('filterNotifByStatus', (event, arg1) => {
      this.filterNotifByUserByStatus(arg1);
    });
    $(document).on('filterNotifByUserByStatus', (event, arg1) => {
      this.filterNotifByUserByStatus(arg1);
    });

    $('#toStart', this._root).on('click', (e) => todos.goToStart());
    $('#toPreviousPage', this._root).on('click', (e) =>
      todos.goToPreviousPage(),
    );
    $('#toNextPage', this._root).on('click', (e) => todos.goToNextPage());
    $('#toEnd', this._root).on('click', (e) => todos.goToLastPage());

    if (!this.popupModeOrControl) {
      setTimeout((o: any) => this.start(), 10);
    }
    return this._root;
  }

  private updateNavState() {
    const todos = this.pagedTodos;
    const str =
      todos.getTotalPages() > 0
        ? `Page ${todos.getCurrentPage()} of ${todos.getTotalPages()}`
        : `No data to display`;
    $('#PageText').html(str);

    $('#toStart', this._root).prop('disabled', todos.isStartDisabled());
    $('#toPreviousPage', this._root).prop(
      'disabled',
      todos.isPreviousDisabled(),
    );
    $('#toNextPage', this._root).prop('disabled', todos.isNextDisabled());
    $('#toEnd', this._root).prop('disabled', todos.isLastDisabled());
  }

  installCopyButtons(title: string) {
    $('#NOOTableHeader i').remove();
    sdkInstance.ml.UI.copyBuffer(
      $('#NOOTableHeader'),
      'copy list to clipboard',
      $('#NOOContent'),
      this._root,
      (copied: JQuery) => {
        $('.doNotCopy', copied).remove();
        const filter = $('#NOOInputFilter').val();
        // Different if popup/Control mode
        $('#NOOTitleForCopy', copied).html(
          '<h1>' +
            title +
            '</h1> <span> <b> Date:</b> ' +
            sdkInstance.ml.UI.DateTime.renderCustomerHumanDate(new Date()) +
            '<br/>' +
            (filter != '' ? '<b>Filter : </b>' + filter + '<br/>' : '') +
            '<br/>',
        );
      },
    );

    $('#NOOPieChartTitle i').remove();
    sdkInstance.ml.UI.copyBuffer(
      $('#NOOPieChartTitle'),
      'copy  to clipboard',
      $('#overviewGraph'),
      this._root,
      (copied: JQuery) => {
        sdkInstance.ml.UI.fixC3ForCopy(copied);
      },
    );

    $('#NOOBarChartTitle i').remove();
    sdkInstance.ml.UI.copyBuffer(
      $('#NOOBarChartTitle'),
      'copy  to clipboard',
      $('#overviewPerUser'),
      this._root,
      (copied: JQuery) => {
        sdkInstance.ml.UI.fixC3ForCopy(copied);
      },
    );
  }
  private drawNotifCharts(inputData: Todo[]) {
    // Create the data table.
    const mySet = inputData.map((x) => x.login);
    const users = [];
    for (let i = 0; i < mySet.length; i++) {
      if (users.indexOf(mySet[i]) < 0) {
        users.push(mySet[i]);
      }
    }

    const groups = ['Done', 'Pending'];
    const convertedData: Array<Record<string, c3.Primitive>> = [];

    //var usersData = [["User", "Passed", "Failed", "New", "Progress", { role: 'annotation' }]]
    for (let i = 0; i < users.length; i++) {
      const user = users[i];
      const line: Record<string, c3.Primitive> = { user: user };
      line['Done'] = inputData.filter((elem) => {
        return elem.login == user && elem.closedAt != undefined;
      }).length;
      line['Pending'] = inputData.filter((elem) => {
        return elem.login == user && elem.closedAt == undefined;
      }).length;
      convertedData.push(line);
    }

    //Calculate width
    const containerWidth = $('#overviewPerUser').width();

    const itemWidth = containerWidth / 15;

    let graphWidth = -1;

    if (users.length > 15) {
      graphWidth = itemWidth * (users.length + 1);
      $('#overviewPerUser').css('overflow-x', 'auto');
      $('#overviewPerUser').css('overflow-y', 'hidden');
    }

    c3.generate({
      bindto: '#overviewPerUser',
      size: {
        width: graphWidth != -1 ? graphWidth : undefined,
      },
      data: {
        json: convertedData,
        keys: {
          x: 'user', // it's possible to specify 'x' when category axis
          value: groups,
        },
        type: 'bar',
        groups: [groups],
        colors: {
          Done: sdkInstance.UIToolsConstants.CIColors.GreenSushi.color,
          Pending: sdkInstance.UIToolsConstants.CIColors.RedPersimmon.color,
        },
        onclick: (d, element) => {
          $(document).trigger('filterNotifByUserByStatus', [
            { user: convertedData[d.index].user, status: d.id },
          ]);
        },
      },
      axis: {
        x: {
          type: 'category',
          tick: {
            fit: true,
            rotate: 65,
            multiline: false,
            multilineMax: 1,
          },
        },
      },
      legend: {
        position: 'inset',
        inset: {
          anchor: graphWidth == -1 ? 'top-right' : 'top-left',
        },
      },
      zoom: {
        enabled: false,
        rescale: false,
        extent: [1, 5],
      },
    });
  }

  private drawCumulativeChart(lst: Todo[]): any {
    let data: { date: string[]; created: string[]; remainings: string[] };
    data = DashboardPage.calculateCumulativeChart(lst);

    c3.generate({
      bindto: '#cumulativeChart',
      data: {
        columns: <Array<[string, ...c3.PrimitiveArray]>>[
          data.date,
          data.created,
          data.remainings,
        ],
        x: 'Date',
        colors: {
          Created: sdkInstance.UIToolsConstants.CIColors.GreenSushi.color,
          Pending: sdkInstance.UIToolsConstants.CIColors.RedPersimmon.color,
        },
      },
      axis: {
        x: {
          type: 'timeseries',
          tick: {
            format: '%Y-%m-%d',
            count: 10,
            fit: true,
          },
        },
      },
      point: {
        show: false,
      },
      zoom: {
        enabled: false,
        rescale: true,
        extent: [1, 5],
      },
    });
    $('#cumulativeChart .c3-event-rects').click(() => {
      $(document).trigger('filterNotifByUserByStatus', [
        { user: '', status: '' },
      ]);
    });
  }

  onResize() {
    /* Will be triggered when resizing. */
  }

  private dashboardDOM = `<div class="panel-body-v-scroll fillHeight">
        <style>
        .chart {
            width: 90%;
            min-height: 350px;
            cursor:pointer;
        }
        .bigChart
        {
            min-height:700px;
        }
        </style>
        <div  style="margin:10px;">
            <div class="row product doNotCopy">
                <div id="itemSelectionNOO" class="col-lg-12 col-md-5 col-md-offset-0"></div>
            </div>
            <div class="row" id="waitForIt" class="spinningWait">

            </div>
            <div id="NOONoItems">Not enough data to display Notification Overview </div>
        </div>
        <div id="NOOContent" class="" style="margin:10px;" >

            <div class="row doNotCopy">
                <div class="col-lg-6 ">
                    <div class="panel panel-default">
                        <div class="panel-heading">
                            <h3 class="panel-title" id="">Overview</h3>
                        </div>
                        <div class="panel-body">
                            <div id="cumulativeChart" class="chart"></div>
                        </div>
                    </div>
                </div>
                <div  class="col-lg-6">
                    <div class="panel panel-default">
                        <div class="panel-heading">
                            <h3 class="panel-title" id="NOOBarChartTitle">Overview per user</h3>
                        </div>
                        <div class="panel-body">
                            <div id="overviewPerUser" class="chart" ></div>
                        </div>
                    </div>
            </div>
            </div>
            <div class="row" id="NOOTitleForCopy">
            </div>
            <div class="row doNotCopy">
                <div class="col-lg-3 ">
                    <h3 id="NOOTableHeader">Notifications list</h3>
                </div>
                <div class=" col-lg-7">
                </div>
                <div class=" col-lg-2">
                    <input type="text" id="NOOInputFilter" style="margin-bottom:10px;" placeholder="filter..." class="doNotCopy  form-control"></input>
                </div>
            </div>
            <div class="table-responsive">
                <table class="table table-condensed table-borderless table-hover" id="NOOTable">
                    <thead>
                        <tr>
                            <th style='width:20px; '></th>
                            <th style="min-width: 65px;">ID<span></span></th>
                            <th>Date</th>
                            <th>Description</th>
                            <th>Assignee</th>
                            <th>Assigned by</th>
                        </tr>
                    </thead>
                    <tbody id="NotifList">

                        <tr id="NotifRow" class="hidden">
                            <td data-attr="type"></td>
                            <td data-attr="itemRef">Notif-XXX</td>
                            <td data-attr="date">Verify wheels count</td>
                            <td data-attr="title">Verify wheels count</td>
                            <td data-attr="assignee">Francois</td>
                            <td data-attr="assignedBy"></td>
                        </tr>

                    </tbody>
                </table>
            </div>
            <div id="NavPanel">
                <button id="toStart" class="btn btn-sm"><<</button>
                <button id="toPreviousPage" class="btn btn-sm"><</button>
                <span id="PageText">Page 1 of X</span>
                <button id="toNextPage" class="btn btn-sm">></button>
                <button id="toEnd" class="btn btn-sm">>></button>
            <div>

        </div></div> `;
}
